import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { apiEndpoints } from '../../../api/endpoints';
import Navbar from '../../layout/Navbar';
import { jobApi } from '../../../api/job';
import { testcaseApi } from '../../../api/testcase';
import { testsuiteApi } from '../../../api/testsuite';
import DataFixture from '../../sections/DataFixture';
import { useToast } from '../../ui/use-toast';

function TestCaseList() {
  const [testCases, setTestCases] = useState([]);
  const [testSuite, setTestSuite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { suiteId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedTestCases, setSelectedTestCases] = useState(new Set());
  const [testSuites, setTestSuites] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedTestCaseToDuplicate, setSelectedTestCaseToDuplicate] = useState(null);
  const [targetSuiteId, setTargetSuiteId] = useState('');
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    // Get sort preference from localStorage with fallback
    return localStorage.getItem('testCaseSortType') || 'updated_desc';
  });

  // Add state for fixture modal
  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);

  // Add state for filtering
  const [filter, setFilter] = useState('all'); // 'all', 'enabled', 'disabled'
  const [isExporting, setIsExporting] = useState(false);
  
  // Track if we've initialized sort from server (prevents double API calls)
  const sortInitialized = useRef(false);

  // Handle fallback for first-time users without localStorage
  useEffect(() => {
    const cachedSort = localStorage.getItem('testCaseSortType');
    if (!cachedSort) {
      // First visit on this device - show skeleton loader and fetch from server
      // The server response will include sort_settings which gets handled in fetchTestSuiteAndCases
    }
  }, []);

  useEffect(() => {
    if (!suiteId) {
      console.error('No suite ID provided');
      navigate('/testsuites');
      return;
    }
    fetchTestSuiteAndCases();
  }, [suiteId, sortBy]);

  // Lazy load test suites only when duplicate modal is opened
  const fetchTestSuites = useCallback(async () => {
    if (testSuites.length === 0) {
      try {
        const response = await testsuiteApi.getTestSuiteNames();
        setTestSuites(response || []);
      } catch (error) {
        console.error('Error fetching test suites:', error);
      }
    }
  }, [testSuites.length]);

  // Add functions for disable/enable functionality
  const toggleTestStatus = async (testId, isDisabled) => {
    try {
      await testcaseApi.toggleTestStatus(testId, isDisabled);
      toast({
        title: "Success",
        description: `Test ${isDisabled ? 'disabled' : 'enabled'} successfully`
      });
      fetchTestSuiteAndCases();
    } catch (error) {
      console.error('Error toggling test status:', error);
      toast({
        title: "Error",
        description: `Failed to ${isDisabled ? 'disable' : 'enable'} test: ` + error.message
      });
    }
  };

  const batchToggleStatus = async (isDisabled) => {
    if (selectedTestCases.size === 0) {
      toast({
        title: "Warning",
        description: "Please select test cases first"
      });
      return;
    }

    const action = isDisabled ? 'disable' : 'enable';
    const confirmMessage = `Are you sure you want to ${action} ${selectedTestCases.size} test case(s)?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await testcaseApi.batchToggleStatus(suiteId, Array.from(selectedTestCases), isDisabled);
      toast({
        title: "Success",
        description: `Successfully ${action}d ${selectedTestCases.size} test case(s)`
      });
      setSelectedTestCases(new Set());
      fetchTestSuiteAndCases();
    } catch (error) {
      console.error(`Error ${action}ing test cases:`, error);
      toast({
        title: "Error", 
        description: `Failed to ${action} test cases: ` + error.message
      });
    }
  };

  const fetchTestStatistics = useCallback(async () => {
    try {
      await testcaseApi.getTestStatistics(suiteId);
      // Statistics are handled in the counts calculated from testCases
    } catch (error) {
      console.error('Error fetching test statistics:', error);
    }
  }, [suiteId]);

  // Filter tests based on current filter
  const filteredTestCases = testCases.filter(test => {
    switch (filter) {
      case 'enabled':
        return !test.is_disabled;
      case 'disabled':
        return test.is_disabled;
      default:
        return true;
    }
  });

  // Count enabled and disabled tests for display
  const enabledCount = testCases.filter(test => !test.is_disabled).length;
  const disabledCount = testCases.filter(test => test.is_disabled).length;

  const fetchTestSuiteAndCases = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get the test cases for this suite with sorting
      const response = await apiEndpoints.getTestCases(suiteId, sortBy);
      
      // Set test cases from the new response format
      setTestCases(response.test_cases || []);
      
      // Update sort state from response if provided and localStorage is empty
      if (response.sort_settings?.current_sort && !sortInitialized.current) {
        const cachedSort = localStorage.getItem('testCaseSortType');
        if (!cachedSort) {
          // First visit - use server preference and cache it
          setSortBy(response.sort_settings.current_sort);
          localStorage.setItem('testCaseSortType', response.sort_settings.current_sort);
        }
        sortInitialized.current = true;
      }
      
      // Set the test suite info from the first test case
      if (response.test_cases && response.test_cases.length > 0) {
        setTestSuite({
          suite_name: `Test Cases`,
          description: ''
        });
      }
      
      setLoading(false);
      
      // Fetch test statistics after loading test cases
      await fetchTestStatistics();
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response);
      setError(error.message || 'Failed to load test cases');
      setLoading(false);
      if (error.message.includes('session has expired')) {
        navigate('/login');
      }
    }
  }, [suiteId, sortBy, navigate, fetchTestStatistics]);

  const handleRunTest = async (testId) => {
    setSelectedTestId(testId);
    setIsFixtureModalOpen(true);
  };

  const handleFixtureSelect = async (fixture) => {
    try {
      const response = await jobApi.startTestJobWithFixture(suiteId, selectedTestId, fixture.id);
      
      // Navigate to job status page with the job ID
      navigate(`/job/${response.id}`);
    } catch (error) {
      setError('Failed to start test job: ' + error.message);
    } finally {
      setIsFixtureModalOpen(false);
      setSelectedTestId(null);
    }
  };

  const handleRunWithoutFixture = async () => {
    try {
      // Using the same startTestJobWithFixture function but without passing a fixture ID
      const response = await jobApi.startTestJobWithFixture(suiteId, selectedTestId);
      
      // Navigate to job status page with the job ID
      navigate(`/job/${response.id}`);
    } catch (error) {
      setError('Failed to start test job: ' + error.message);
    } finally {
      setIsFixtureModalOpen(false);
      setSelectedTestId(null);
    }
  };

  const handleTestCaseSelection = (testCaseId) => {
    setSelectedTestCases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testCaseId)) {
        newSet.delete(testCaseId);
      } else {
        newSet.add(testCaseId);
      }
      return newSet;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedTestCases.size === 0) {
      toast({
        title: "Warning",
        description: "Please select at least one test case to delete"
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedTestCases.size} test case(s)?`)) {
      return;
    }

    try {
      const response = await testcaseApi.deleteTestCases(suiteId, Array.from(selectedTestCases));
      toast({
        title: "Success",
        description: response.message
      });
      setError(null);
      fetchTestSuiteAndCases();
      setSelectedTestCases(new Set());
    } catch (error) {
      console.error('Error deleting test cases:', error);
      toast({
        title: "Error",
        description: "Failed to delete test cases: " + error.message
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDuplicateClick = (testCase) => {
    setSelectedTestCaseToDuplicate(testCase);
    setTargetSuiteId(suiteId); // Default to current suite
    setShowDuplicateModal(true);
    fetchTestSuites(); // Lazy load test suites when modal opens
  };

  const handleDuplicateSelected = () => {
    if (selectedTestCases.size === 0) {
      toast({
        title: "Warning",
        description: "Please select at least one test case to duplicate"
      });
      return;
    }
    
    // Default to current suite
    setTargetSuiteId(suiteId);
    setShowDuplicateModal(true);
    fetchTestSuites(); // Lazy load test suites when modal opens
  };

  const handleExportJson = async () => {
    const idsToExport = selectedTestCases.size > 0
      ? Array.from(selectedTestCases)
      : filteredTestCases.map(t => t.id);

    if (idsToExport.length === 0) return;

    try {
      setIsExporting(true);
      const [suite, ...results] = await Promise.all([
        testsuiteApi.getTestSuiteById(suiteId),
        ...idsToExport.map(id => testcaseApi.getTestCaseById(id))
      ]);
      const suiteName = (suite?.suite_name || 'testcases').replace(/[^a-zA-Z0-9_\-]/g, '_');
      const data = results.map(tc => ({
        testname: tc.testname,
        steps: (tc.steps || [])
          .filter(s => s.metadata?.actionType !== 'apicall')
          .sort((a, b) => a.order - b.order)
          .map(s => s.name)
      }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${suiteName}_testcases.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export test cases: ' + error.message });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDuplicateTestCase = async () => {
    if (!targetSuiteId) {
      toast({
        title: "Warning",
        description: "Please select a target test suite"
      });
      return;
    }

    try {
      setDuplicateLoading(true);
      
      // If we're duplicating a single test case that was clicked directly
      if (selectedTestCaseToDuplicate) {
        await testcaseApi.copyTestCase(selectedTestCaseToDuplicate.id, targetSuiteId);
        toast({
          title: "Success",
          description: `Successfully duplicated test case "${selectedTestCaseToDuplicate.testname}"`
        });
      } 
      // If we're duplicating multiple selected test cases
      else if (selectedTestCases.size > 0) {
        await testcaseApi.duplicateTestCases(
          suiteId, 
          Array.from(selectedTestCases), 
          targetSuiteId
        );
        toast({
          title: "Success", 
          description: `Successfully duplicated ${selectedTestCases.size} test case(s)`
        });
        setSelectedTestCases(new Set()); // Clear selection
      }
      
      setShowDuplicateModal(false);
      setDuplicateLoading(false);
      setSelectedTestCaseToDuplicate(null);
      
      // Refresh the list if duplicating to the same suite
      if (targetSuiteId === suiteId) {
        fetchTestSuiteAndCases();
      }
    } catch (error) {
      console.error('Error duplicating test cases:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate test cases: " + error.message
      });
      setDuplicateLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Link 
                to="/testsuites" 
                className="text-gray-600 hover:text-black"
              >
                Test Suites
              </Link>
              <span className="text-gray-600">/</span>
              <h1 className="text-2xl font-bold text-gray-900">
                {testSuite ? testSuite.suite_name : 'Test Cases'}
              </h1>
            </div>
            {testSuite && (
              <p className="text-gray-600 mt-1">{testSuite.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <div className="flex flex-wrap bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    filter === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All ({testCases.length})
                </button>
                <button
                  onClick={() => setFilter('enabled')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    filter === 'enabled'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Enabled ({enabledCount})
                </button>
                <button
                  onClick={() => setFilter('disabled')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    filter === 'disabled'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Disabled ({disabledCount})
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4 flex-shrink-0">
            {selectedTestCases.size > 0 && (
              <>
                <div className="flex border border-gray-300 rounded-md overflow-hidden">
                  <button
                    onClick={() => batchToggleStatus(true)}
                    className="bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 flex items-center gap-2 transition-colors border-r border-gray-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Disable
                  </button>
                  
                  <button
                    onClick={() => batchToggleStatus(false)}
                    className="bg-green-50 hover:bg-green-100 text-green-700 px-4 py-2 flex items-center gap-2 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Enable
                  </button>
                </div>

                <button
                  onClick={handleDeleteSelected}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Selected ({selectedTestCases.size})
                </button>
                
                <button
                  onClick={handleDuplicateSelected}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Duplicate Selected ({selectedTestCases.size})
                </button>
              </>
            )}
            <button
              onClick={handleExportJson}
              disabled={isExporting}
              className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              Export JSON
            </button>
            <Link
              to={`/testsuites/${suiteId}/tests/create`}
              className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Create New Test Case
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading test cases...</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                {error}
              </div>
            )}

            {testCases.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <p className="text-gray-600">No test cases found for this test suite.</p>
                <Link
                  to={`/testsuites/${suiteId}/tests/create`}
                  className="text-black hover:text-gray-700 mt-4 inline-block"
                >
                  Create your first test case
                </Link>
              </div>
            ) : filteredTestCases.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <p className="text-gray-600">
                  No {filter === 'enabled' ? 'enabled' : filter === 'disabled' ? 'disabled' : ''} test cases found.
                </p>
                <button
                  onClick={() => setFilter('all')}
                  className="text-black hover:text-gray-700 mt-2 inline-block hover:underline"
                >
                  Show all tests
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4 flex justify-end">
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      const newSort = e.target.value;
                      setSortBy(newSort);
                      // Cache the preference in localStorage for fast UX
                      localStorage.setItem('testCaseSortType', newSort);
                      // Note: API call will be triggered by useEffect dependency on sortBy
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    <option value="updated_desc">Most Recently Updated</option>
                    <option value="name_asc">Test Name (A-Z)</option>
                    <option value="name_desc">Test Name (Z-A)</option>
                  </select>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Desktop: table layout */}
                  <div className="hidden xl:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedTestCases.size > 0 && selectedTestCases.size === testCases.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTestCases(new Set(testCases.map(t => t.id)));
                                } else {
                                  setSelectedTestCases(new Set());
                                }
                              }}
                            />
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Test Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created At
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTestCases.map((test) => (
                          <tr key={test.id} className={`hover:bg-gray-50 ${test.is_disabled ? 'opacity-60' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={selectedTestCases.has(test.id)}
                                onChange={() => handleTestCaseSelection(test.id)}
                              />
                            </td>
                            <td className="px-6 py-4">
                              <div
                                className={`text-sm font-medium break-words ${test.is_disabled ? 'text-gray-500 line-through' : 'text-gray-900'}`}
                                title={test.testname}
                              >
                                {test.testname}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => toggleTestStatus(test.id, !test.is_disabled)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    test.is_disabled
                                      ? 'bg-gray-200 focus:ring-gray-500'
                                      : 'bg-green-600 focus:ring-green-500'
                                  }`}
                                  title={test.is_disabled ? 'Click to enable' : 'Click to disable'}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                                      test.is_disabled ? 'translate-x-1' : 'translate-x-6'
                                    }`}
                                  />
                                </button>
                                <span className={`text-sm font-medium ${
                                  test.is_disabled ? 'text-gray-500' : 'text-green-700'
                                }`}>
                                  {test.is_disabled ? 'Disabled' : 'Enabled'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {formatDate(test.created_at)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleRunTest(test.id)}
                                  disabled={test.is_disabled}
                                  className={`px-3 py-1 rounded-md flex items-center gap-1 transition-colors ${
                                    test.is_disabled
                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                      : 'bg-green-600 hover:bg-green-700 text-white'
                                  }`}
                                  title={test.is_disabled ? 'Cannot run disabled test' : 'Run test'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Run Test
                                </button>
                                <button
                                  onClick={() => navigate(`/testcase/${test.id}/edit`)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDuplicateClick(test)}
                                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  Duplicate
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Narrow screens: card layout with full text and wrapping buttons */}
                  <div className="xl:hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                        checked={selectedTestCases.size > 0 && selectedTestCases.size === testCases.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTestCases(new Set(testCases.map(t => t.id)));
                          } else {
                            setSelectedTestCases(new Set());
                          }
                        }}
                      />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Select All</span>
                    </div>
                    <div className="divide-y divide-gray-200">
                    {filteredTestCases.map((test) => (
                      <div
                        key={test.id}
                        className={`px-4 py-4 hover:bg-gray-50 ${test.is_disabled ? 'opacity-60' : ''}`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5 flex-shrink-0"
                              checked={selectedTestCases.has(test.id)}
                              onChange={() => handleTestCaseSelection(test.id)}
                            />
                            <div className="min-w-0 flex-1">
                              <div
                                className={`text-sm font-medium break-words ${test.is_disabled ? 'text-gray-500 line-through' : 'text-gray-900'}`}
                              >
                                {test.testname}
                              </div>
                              <div className="text-sm text-gray-500 mt-0.5">
                                {formatDate(test.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleTestStatus(test.id, !test.is_disabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                  test.is_disabled
                                    ? 'bg-gray-200 focus:ring-gray-500'
                                    : 'bg-green-600 focus:ring-green-500'
                                }`}
                                title={test.is_disabled ? 'Click to enable' : 'Click to disable'}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                                    test.is_disabled ? 'translate-x-1' : 'translate-x-6'
                                  }`}
                                />
                              </button>
                              <span className={`text-sm font-medium ${
                                test.is_disabled ? 'text-gray-500' : 'text-green-700'
                              }`}>
                                {test.is_disabled ? 'Disabled' : 'Enabled'}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => handleRunTest(test.id)}
                                disabled={test.is_disabled}
                                className={`px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm ${
                                  test.is_disabled
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-green-600 hover:bg-green-700 text-white'
                                }`}
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Run Test
                              </button>
                              <button
                                onClick={() => navigate(`/testcase/${test.id}/edit`)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                onClick={() => handleDuplicateClick(test)}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Duplicate
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-w-full">
            <h3 className="text-lg font-bold mb-4">
              {selectedTestCaseToDuplicate 
                ? `Duplicate Test Case` 
                : `Duplicate ${selectedTestCases.size} Test Case(s)`}
            </h3>
            <p className="mb-4">
              {selectedTestCaseToDuplicate 
                ? `Duplicate "${selectedTestCaseToDuplicate.testname}" to:` 
                : `Duplicate ${selectedTestCases.size} test case(s) to:`}
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Test Suite
              </label>
              <select
                value={targetSuiteId}
                onChange={(e) => setTargetSuiteId(e.target.value)}
                className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
              >
                <option value="">Select a test suite</option>
                {testSuites.map(suite => (
                  <option key={suite.id} value={suite.id}>
                    {suite.suite_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDuplicateTestCase}
                disabled={duplicateLoading || !targetSuiteId}
                className={`px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center gap-2 ${
                  duplicateLoading || !targetSuiteId ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {duplicateLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent"></div>
                    Duplicating...
                  </>
                ) : (
                  'Duplicate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <DataFixture 
        isOpen={isFixtureModalOpen}
        onClose={() => {
          setIsFixtureModalOpen(false);
          setSelectedTestId(null);
        }}
        onSelect={handleFixtureSelect}
        onRunWithoutFixture={handleRunWithoutFixture}
        title="Select Test Data Fixture"
        description="Select a data fixture to use for this test run or run without a fixture."
      />
    </div>
  );
}

export default TestCaseList; 